class HomeVideoCarousel extends HTMLElement {
    constructor() {
        super();
        this.observer = null;
        this.cartDrawerObserver = null;
        this.sectionId = this.dataset.sectionId || '';
        this.modal = document.getElementById(`VideoModal-${this.sectionId}`) || this.querySelector('.home-video-carousel__modal');
        this.modalVideo = this.modal?.querySelector('video');
        this.closeBtn = this.modal?.querySelector('.home-video-carousel__close-btn');
        this.muteBtn = this.modal?.querySelector('.home-video-carousel__mute-btn');
        this.prevBtn = this.modal?.querySelector('.home-video-carousel__nav-btn--prev');
        this.nextBtn = this.modal?.querySelector('.home-video-carousel__nav-btn--next');
        
        // Swipe navigation properties
        this.currentVideoIndex = 0;
        this.videoTriggers = [];
        this.touchStartY = 0;
        this.touchEndY = 0;
        this.swipeThreshold = 50; // Minimum distance for swipe
        this.isSwiping = false;
        this.modalMedia = null; // Reference to modal media container
        this.modalInfo = null; // Reference to modal info container
        
        // Store scroll position to prevent scroll-to-top on close
        this.scrollPosition = 0;
        this.openingTrigger = null;
        
        // Bind swipe handlers for proper removal
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);

        this.bindEvents();
    }

    connectedCallback() {
        this.initObserver();
    }

    bindEvents() {
        if (!this.modal) return;

        this.querySelectorAll('[data-modal-trigger]').forEach(trigger => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.openModal(e.currentTarget);
            });
        });

        this.closeBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.closeModal();
        });

        this.muteBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleMute();
        });

        this.prevBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigateToPreviousVideo();
        });

        this.nextBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigateToNextVideo();
        });

        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) this.closeModal();
        });

        this.modal.addEventListener('close', () => this.closeModal());
    }

    openModal(trigger) {
        // Store current scroll position and trigger element
        this.scrollPosition = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        this.openingTrigger = trigger;
        
        // Get all video triggers for swipe navigation
        this.videoTriggers = Array.from(this.querySelectorAll('[data-modal-trigger]'));
        this.currentVideoIndex = this.videoTriggers.indexOf(trigger);
        
        // Load the initial video
        this.loadVideo(trigger);

        this.modal.showModal();
        document.body.style.overflow = 'hidden';
        
        // Immediately restore scroll position to prevent scroll-to-top
        // showModal() can cause the page to scroll, so we restore it right away
        requestAnimationFrame(() => {
            window.scrollTo(0, this.scrollPosition);
        });

        // Initialize swipe navigation for mobile
        if (window.innerWidth <= 749) {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                this.initSwipeNavigation();
            }, 100);
        }

        // Watch for cart drawer opening
        this.observeCartDrawer();
    }

    loadVideo(trigger) {
        if (!this.modalVideo) return;

        // Get video URL from data attribute
        const videoUrl = trigger.dataset.videoUrl;

        if (videoUrl) {
            // Clear existing sources
            this.modalVideo.innerHTML = '';
            this.modalVideo.removeAttribute('src');

            // Set the video source directly
            this.modalVideo.src = videoUrl;

            // Load and configure the video
            this.modalVideo.load();
            this.modalVideo.muted = false;
            this.modalVideo.currentTime = 0;

            // Play with error handling
            const playPromise = this.modalVideo.play();
            if (playPromise !== undefined) {
                playPromise.catch(e => {
                    console.error('Video play failed:', e);
                    // Try playing muted if unmuted fails
                    this.modalVideo.muted = true;
                    this.modalVideo.play().catch(err => console.error('Muted play also failed:', err));
                });
            }
        }

        // Setup Product Info
        const blockId = trigger.dataset.blockId;
        const template = document.getElementById(`ProductInfo-${blockId}`);
        const infoContainer = this.modal.querySelector('.home-video-carousel__modal-info');

        if (template && infoContainer) {
            infoContainer.innerHTML = '';
            const content = template.content.cloneNode(true);
            infoContainer.appendChild(content);
            infoContainer.style.display = 'flex'; // Show product info
            
            // Re-query modalInfo after content is injected (for swipe navigation)
            if (window.innerWidth <= 749) {
                this.modalInfo = infoContainer;
            }
        } else {
            // No product linked - hide product info container and show only video
            if (infoContainer) {
                infoContainer.innerHTML = '';
                infoContainer.style.display = 'none';
            }
        }
        
        // Update navigation buttons state
        this.updateNavigationButtons();
    }

    observeCartDrawer() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        this.cartDrawerObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    if (cartDrawer.classList.contains('active')) {
                        this.closeModal();
                    }
                }
            });
        });

        this.cartDrawerObserver.observe(cartDrawer, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    closeModal() {
        if (this.modal.open) {
            this.modal.close();
        }

        // Remove swipe navigation
        this.removeSwipeNavigation();

        // Disconnect cart drawer observer
        if (this.cartDrawerObserver) {
            this.cartDrawerObserver.disconnect();
            this.cartDrawerObserver = null;
        }

        if (this.modalVideo) {
            this.modalVideo.pause();
            this.modalVideo.currentTime = 0;
            this.modalVideo.innerHTML = '';
            this.modalVideo.removeAttribute('src');
        }

        document.body.style.overflow = '';
        
        // Restore scroll position to prevent scroll-to-top
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
            window.scrollTo(0, this.scrollPosition);
        });
        
        // Clear stored trigger
        this.openingTrigger = null;
    }

    initSwipeNavigation() {
        if (!this.modal || !this.modalVideo) return;
        
        // Get modal media and info containers for animation
        this.modalMedia = this.modal.querySelector('.home-video-carousel__modal-media');
        this.modalInfo = this.modal.querySelector('.home-video-carousel__modal-info');
        
        // Reset transforms on init
        if (this.modalMedia) {
            this.modalMedia.style.transform = 'translateY(0)';
        }
        if (this.modalInfo) {
            this.modalInfo.style.transform = 'translateY(0)';
        }

        // Touch start
        this.modal.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        // Touch move
        this.modal.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        // Touch end
        this.modal.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }

    removeSwipeNavigation() {
        if (!this.modal) return;

        this.modal.removeEventListener('touchstart', this.handleTouchStart);
        this.modal.removeEventListener('touchmove', this.handleTouchMove);
        this.modal.removeEventListener('touchend', this.handleTouchEnd);
    }

    handleTouchStart(e) {
        // Don't start swipe if touching a button or link (but allow swiping on info area itself)
        const target = e.target;
        if (target.closest('button') || target.closest('a')) {
            return;
        }
        
        this.touchStartY = e.touches[0].clientY;
        this.isSwiping = true;
        this.currentTranslate = 0;
        
        // Reset transform - disable transitions for smooth following
        if (this.modalMedia) {
            this.modalMedia.style.transition = 'none';
            this.modalMedia.style.transform = 'translateY(0)';
        }
        if (this.modalInfo) {
            this.modalInfo.style.transition = 'none';
            this.modalInfo.style.transform = 'translateY(0)';
        }
    }

    handleTouchMove(e) {
        if (!this.isSwiping || !this.modalMedia) return;
        
        const touchY = e.touches[0].clientY;
        const diff = touchY - this.touchStartY;
        
        // If swiping vertically significantly, prevent default to avoid page scroll
        if (Math.abs(diff) > 10) {
            e.preventDefault();
            
            // Move video and info with finger
            this.currentTranslate = diff;
            this.modalMedia.style.transform = `translateY(${diff}px)`;
            if (this.modalInfo) {
                this.modalInfo.style.transform = `translateY(${diff}px)`;
            }
        }
    }

    handleTouchEnd(e) {
        if (!this.isSwiping) return;
        
        this.touchEndY = e.changedTouches[0].clientY;
        const swipeDistance = this.touchEndY - this.touchStartY;
        
        // Get current transform position
        const currentTranslate = this.currentTranslate || 0;
        
        // Determine if we should navigate
        if (Math.abs(swipeDistance) > this.swipeThreshold) {
            // Enable smooth transition from current position (faster)
            if (this.modalMedia) {
                this.modalMedia.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            }
            if (this.modalInfo) {
                this.modalInfo.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            }
            
            // Swipe up (negative) = next video
            if (swipeDistance < 0) {
                this.navigateToNext(currentTranslate);
            }
            // Swipe down (positive) = previous video
            else {
                this.navigateToPrevious(currentTranslate);
            }
        } else {
            // Not enough swipe, snap back with animation
            if (this.modalMedia) {
                this.modalMedia.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalMedia.style.transform = 'translateY(0)';
            }
            if (this.modalInfo) {
                this.modalInfo.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalInfo.style.transform = 'translateY(0)';
            }
        }
        
        this.isSwiping = false;
    }

    navigateToNext(currentTranslate = 0) {
        if (this.currentVideoIndex < this.videoTriggers.length - 1) {
            const viewportHeight = window.innerHeight;
            
            // Continue upward motion - slide current video off screen upward (faster transition)
            if (this.modalMedia) {
                this.modalMedia.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalMedia.style.transform = `translateY(-${viewportHeight}px)`;
            }
            if (this.modalInfo) {
                this.modalInfo.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalInfo.style.transform = `translateY(-${viewportHeight}px)`;
            }
            
            // Wait for animation to complete, then load next video (reduced delay)
            setTimeout(() => {
                this.currentVideoIndex++;
                const nextTrigger = this.videoTriggers[this.currentVideoIndex];
                this.loadVideo(nextTrigger);
                
                // Position new video at bottom (off-screen below)
                if (this.modalMedia) {
                    this.modalMedia.style.transition = 'none';
                    this.modalMedia.style.transform = `translateY(${viewportHeight}px)`;
                }
                if (this.modalInfo) {
                    this.modalInfo.style.transition = 'none';
                    this.modalInfo.style.transform = `translateY(${viewportHeight}px)`;
                }
                
                // Animate new video sliding in from bottom (faster)
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (this.modalMedia) {
                            this.modalMedia.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                            this.modalMedia.style.transform = 'translateY(0)';
                        }
                        if (this.modalInfo) {
                            this.modalInfo.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                            this.modalInfo.style.transform = 'translateY(0)';
                        }
                    });
                });
            }, 250);
        } else {
            // At the end, snap back smoothly
            if (this.modalMedia) {
                this.modalMedia.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalMedia.style.transform = 'translateY(0)';
            }
            if (this.modalInfo) {
                this.modalInfo.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalInfo.style.transform = 'translateY(0)';
            }
        }
    }

    navigateToPrevious(currentTranslate = 0) {
        if (this.currentVideoIndex > 0) {
            const viewportHeight = window.innerHeight;
            
            // Continue downward motion - slide current video off screen downward (faster transition)
            if (this.modalMedia) {
                this.modalMedia.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalMedia.style.transform = `translateY(${viewportHeight}px)`;
            }
            if (this.modalInfo) {
                this.modalInfo.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalInfo.style.transform = `translateY(${viewportHeight}px)`;
            }
            
            // Wait for animation to complete, then load previous video (reduced delay)
            setTimeout(() => {
                this.currentVideoIndex--;
                const prevTrigger = this.videoTriggers[this.currentVideoIndex];
                this.loadVideo(prevTrigger);
                
                // Position new video at top (off-screen above)
                if (this.modalMedia) {
                    this.modalMedia.style.transition = 'none';
                    this.modalMedia.style.transform = `translateY(-${viewportHeight}px)`;
                }
                if (this.modalInfo) {
                    this.modalInfo.style.transition = 'none';
                    this.modalInfo.style.transform = `translateY(-${viewportHeight}px)`;
                }
                
                // Animate new video sliding in from top (faster)
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (this.modalMedia) {
                            this.modalMedia.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                            this.modalMedia.style.transform = 'translateY(0)';
                        }
                        if (this.modalInfo) {
                            this.modalInfo.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                            this.modalInfo.style.transform = 'translateY(0)';
                        }
                    });
                });
            }, 250);
        } else {
            // At the beginning, snap back smoothly
            if (this.modalMedia) {
                this.modalMedia.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalMedia.style.transform = 'translateY(0)';
            }
            if (this.modalInfo) {
                this.modalInfo.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                this.modalInfo.style.transform = 'translateY(0)';
            }
        }
    }

    toggleMute() {
        if (!this.modalVideo) return;

        this.modalVideo.muted = !this.modalVideo.muted;

        // Update button icons
        if (this.muteBtn) {
            const iconOn = this.muteBtn.querySelector('.icon-sound-on');
            const iconOff = this.muteBtn.querySelector('.icon-sound-off');

            if (this.modalVideo.muted) {
                iconOn.style.display = 'none';
                iconOff.style.display = 'block';
            } else {
                iconOn.style.display = 'block';
                iconOff.style.display = 'none';
            }
        }
    }

    updateNavigationButtons() {
        if (!this.prevBtn || !this.nextBtn) return;
        
        // Disable prev button if at first video
        if (this.currentVideoIndex === 0) {
            this.prevBtn.setAttribute('disabled', 'disabled');
            this.prevBtn.style.opacity = '0.3';
            this.prevBtn.style.cursor = 'not-allowed';
        } else {
            this.prevBtn.removeAttribute('disabled');
            this.prevBtn.style.opacity = '1';
            this.prevBtn.style.cursor = 'pointer';
        }
        
        // Disable next button if at last video
        if (this.currentVideoIndex >= this.videoTriggers.length - 1) {
            this.nextBtn.setAttribute('disabled', 'disabled');
            this.nextBtn.style.opacity = '0.3';
            this.nextBtn.style.cursor = 'not-allowed';
        } else {
            this.nextBtn.removeAttribute('disabled');
            this.nextBtn.style.opacity = '1';
            this.nextBtn.style.cursor = 'pointer';
        }
    }

    navigateToNextVideo() {
        if (this.currentVideoIndex < this.videoTriggers.length - 1) {
            this.currentVideoIndex++;
            const nextTrigger = this.videoTriggers[this.currentVideoIndex];
            this.loadVideo(nextTrigger);
        }
    }

    navigateToPreviousVideo() {
        if (this.currentVideoIndex > 0) {
            this.currentVideoIndex--;
            const prevTrigger = this.videoTriggers[this.currentVideoIndex];
            this.loadVideo(prevTrigger);
        }
    }

    initObserver() {
        const options = {
            root: null,
            rootMargin: '50px',
            threshold: 0.25
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const video = entry.target;
                if (entry.isIntersecting) {
                    video.muted = true;
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(e => {
                            console.log('Autoplay restricted/failed', e);
                        });
                    }
                } else {
                    video.pause();
                }
            });
        }, options);

        const videos = this.querySelectorAll('.home-video-carousel__item video');
        videos.forEach(video => this.observer.observe(video));
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

customElements.define('home-video-carousel', HomeVideoCarousel);

class ModalProductInfo extends HTMLElement {
    constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.blockId = this.dataset.blockId;
        this.productData = null;
        this.currentVariant = null;
    }

    connectedCallback() {
        // Load product data
        this.loadProductData();

        // Initialize variant selection
        this.initializeVariants();
    }

    loadProductData() {
        const productJsonScript = this.querySelector('[data-product-json]');
        if (productJsonScript) {
            try {
                this.productData = JSON.parse(productJsonScript.textContent);
            } catch (e) {
                console.error('Error parsing product JSON:', e);
            }
        }

        const selectedVariantScript = this.querySelector('[data-selected-variant]');
        if (selectedVariantScript) {
            try {
                this.currentVariant = JSON.parse(selectedVariantScript.textContent);
            } catch (e) {
                console.error('Error parsing selected variant:', e);
            }
        }
    }

    initializeVariants() {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;

        const inputs = variantSelects.querySelectorAll('input[type="radio"]');

        inputs.forEach(input => {
            input.addEventListener('change', () => {
                this.onVariantChange();
            });
        });
    }

    onVariantChange() {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects || !this.productData) return;

        // Get all selected options
        const selectedOptions = [];
        const fieldsets = variantSelects.querySelectorAll('fieldset');

        fieldsets.forEach(fieldset => {
            const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
            if (checkedInput) {
                selectedOptions.push(checkedInput.value);

                // Update color name display if this is a color option
                const legend = fieldset.querySelector('legend');
                const colorNameSpan = legend?.querySelector('.selected-color-name');
                if (colorNameSpan) {
                    colorNameSpan.textContent = checkedInput.value;
                }
            }
        });

        // Find matching variant
        const matchingVariant = this.productData.variants.find(variant => {
            return selectedOptions.every((option, index) => {
                const optionKey = `option${index + 1}`;
                return variant[optionKey] === option;
            });
        });

        if (matchingVariant) {
            this.currentVariant = matchingVariant;
            this.updateProductDisplay(matchingVariant);
        }
    }

    updateProductDisplay(variant) {
        // Update hidden input
        const form = this.querySelector('form');
        const input = form?.querySelector('input[name="id"]');
        if (input) {
            input.value = variant.id;

            if (variant.available) {
                input.removeAttribute('disabled');
            } else {
                input.setAttribute('disabled', 'disabled');
            }
        }

        // Update button
        const button = this.querySelector(`#ProductSubmitButton-${this.blockId}`);
        if (button) {
            const buttonTextSpan = button.querySelector('span');
            if (variant.available) {
                button.removeAttribute('disabled');
                if (buttonTextSpan) {
                    buttonTextSpan.textContent = 'Shop Now';
                }
            } else {
                button.setAttribute('disabled', 'disabled');
                if (buttonTextSpan) {
                    buttonTextSpan.textContent = 'Sold out';
                }
            }
        }

        // Update price
        const priceElement = this.querySelector(`#price-${this.blockId}`);
        if (priceElement && variant.price) {
            let priceHTML = '';

            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                // On sale - show sale price and compare price
                priceHTML = `
                    <div class="price-label-wrapper">
                        <span class="price-label">Price</span>
                        <div class="price">
                            <div class="price__container">
                                <div class="price__sale">
                                    <span class="price-item price-item--regular price-item--sale">
                                        ${this.formatMoney(variant.price)}
                                    </span>
                                    <s class="price-item price-item--regular">
                                        ${this.formatMoney(variant.compare_at_price)}
                                    </s>
                                </div>
                            </div>
                        </div>
                    </div>
                `;
            } else {
                // Regular price
                priceHTML = `
                    <div class="price-label-wrapper">
                        <span class="price-label">Price</span>
                        <div class="price">
                            <div class="price__container">
                                <div class="price__regular">
                                    <span class="price-item price-item--regular">
                                        ${this.formatMoney(variant.price)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                `;
            }

            priceElement.innerHTML = priceHTML;
        }

        // Update image if variant has one
        if (variant.featured_image && variant.featured_image.src) {
            const img = this.querySelector('.home-video-carousel__product-thumb');
            if (img) {
                // Use the larger image size
                img.src = variant.featured_image.src;
                img.alt = variant.featured_image.alt || '';
            }
        }
    }

    formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        if (window.Shopify && window.Shopify.formatMoney) {
            return window.Shopify.formatMoney(cents, format);
        }

        let value = (cents / 100).toFixed(2);
        return format.replace(/\{\{\s*amount\s*\}\}/, value).replace(/\{\{\s*amount_no_decimals\s*\}\}/, (cents / 100).toFixed(0));
    }

    disconnectedCallback() {
        // Cleanup if needed
    }
}

customElements.define('modal-product-info', ModalProductInfo);